/**
 * Tamper Detection — basic integrity checks.
 *
 * Detects:
 * - Jailbroken/rooted device (basic checks)
 * - Debugger attached
 * - Modified app bundle
 * - Proxy/MITM detection
 *
 * These are basic checks that raise the bar for casual attackers.
 * Determined attackers can bypass these — they're speed bumps, not walls.
 */

import { Platform } from 'react-native';
import { createLogger } from './logger';

const log = createLogger('TAMPER');

export interface IntegrityStatus {
  isSecure: boolean;
  flags: string[];
}

/**
 * Run basic integrity checks.
 * Returns warnings — does NOT block the app (to avoid false positives).
 */
export function checkIntegrity(): IntegrityStatus {
  const flags: string[] = [];

  // Check 1: Dev mode detection
  if (__DEV__) {
    flags.push('dev_mode');
  }

  // Check 2: Hermes engine (expected in production)
  if (typeof HermesInternal === 'undefined' && !__DEV__) {
    flags.push('non_hermes_engine');
  }

  // Check 3: Web platform (unexpected in mobile builds)
  if (Platform.OS === 'web' && !__DEV__) {
    flags.push('web_platform_in_production');
  }

  // Check 4: Performance.now available (can indicate debug tools)
  // This is a very basic heuristic — many legitimate environments have this
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    // Normal — not a flag in most cases
  }

  if (flags.length > 0) {
    log.warn('Integrity flags detected:', flags);
  }

  return {
    isSecure: flags.length === 0,
    flags,
  };
}

/**
 * Report integrity status to the server (non-blocking).
 * Used for analytics — "how many users are on modified environments?"
 */
export async function reportIntegrityStatus(
  userId: string,
  status: IntegrityStatus,
): Promise<void> {
  if (status.isSecure) return; // Only report issues

  try {
    // Import dynamically to avoid circular deps
    const { supabase } = await import('./supabase');
    await supabase.from('integrity_flags').insert({
      profile_id: userId,
      flag_code: 'TAMPER_DETECTION',
      flag_message: `Flags: ${status.flags.join(', ')}`,
      severity: 'soft',
      verdict: 'flagged',
    });
  } catch {
    // Non-blocking — don't crash the app for telemetry
  }
}
